import { request } from '@umijs/max';
import { getDefaultWorkspaceId } from '@/utils/memberWorkspaceCache';

const SUCCESS_CODE = 200;

/** 资产来源：上传 */
export const MEMBER_ASSET_SOURCE_UPLOAD = 1;
/** 资产来源：AI 生成 */
export const MEMBER_ASSET_SOURCE_AI = 2;

/** 资产类型：文本 */
export const MEMBER_ASSET_TYPE_TEXT = 1;
/** 资产类型：图片 */
export const MEMBER_ASSET_TYPE_IMAGE = 2;
/** 资产类型：视频 */
export const MEMBER_ASSET_TYPE_VIDEO = 3;
/** 资产类型：音频 */
export const MEMBER_ASSET_TYPE_AUDIO = 4;

export type MemberAssetType =
  | typeof MEMBER_ASSET_TYPE_TEXT
  | typeof MEMBER_ASSET_TYPE_IMAGE
  | typeof MEMBER_ASSET_TYPE_VIDEO
  | typeof MEMBER_ASSET_TYPE_AUDIO;

export type ResultResponse<T> = {
  code: number;
  message?: string;
  data?: T;
};

export type MemberAssetUploadResult = {
  id: number;
  relativePath?: string;
  accessUrl?: string;
};

export type MemberAssetPageItem = {
  id: number;
  /** 1-TEXT, 2-IMAGE, 3-VIDEO, 4-AUDIO */
  assetType?: MemberAssetType;
  /** 文本为内容，文件为完整访问 URL */
  assetData: string;
  /** 创建时间 */
  createTime?: string;
};

type MemberAssetPageData = {
  records?: MemberAssetPageItem[];
  total?: number;
};

export type MemberAssetPageResult = {
  records: MemberAssetPageItem[];
  total: number;
};

export type FetchMemberAssetPageParams = {
  assetSource?: typeof MEMBER_ASSET_SOURCE_UPLOAD | typeof MEMBER_ASSET_SOURCE_AI;
  pageNum?: number;
  pageSize?: number;
  workspaceId?: number;
  /** 开始日期，格式 yyyy-MM-dd */
  startTime?: string;
  /** 结束日期，格式 yyyy-MM-dd */
  endTime?: string;
};

/** 会员资产分页 POST /app/member/asset/page */
export async function fetchMemberAssetPageResult(
  params: FetchMemberAssetPageParams,
): Promise<MemberAssetPageResult> {
  const workspaceId = params.workspaceId ?? getDefaultWorkspaceId();
  try {
    const res = await request<ResultResponse<MemberAssetPageData>>('/app/member/asset/page', {
      method: 'POST',
      data: {
        pageNum: params.pageNum ?? 1,
        pageSize: params.pageSize ?? 20,
        ...(params.assetSource != null ? { assetSource: params.assetSource } : {}),
        ...(workspaceId != null ? { workspaceId } : {}),
        ...(params.startTime ? { startTime: params.startTime } : {}),
        ...(params.endTime ? { endTime: params.endTime } : {}),
      },
      skipErrorHandler: true,
    });
    if (res?.code !== SUCCESS_CODE) {
      throw new Error(res?.message?.trim() || '资产列表加载失败');
    }
    return {
      records: res.data?.records ?? [],
      total: res.data?.total ?? 0,
    };
  } catch (error: unknown) {
    const data = (error as { response?: { data?: ResultResponse<MemberAssetPageData> } })?.response
      ?.data;
    if (data?.message) {
      throw new Error(data.message);
    }
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error('资产列表加载失败');
  }
}

/** 会员资产分页（仅 records，兼容 AssetSelector） */
export async function fetchMemberAssetPage(
  params: FetchMemberAssetPageParams & {
    assetSource: typeof MEMBER_ASSET_SOURCE_UPLOAD | typeof MEMBER_ASSET_SOURCE_AI;
  },
): Promise<MemberAssetPageItem[]> {
  const { records } = await fetchMemberAssetPageResult(params);
  return records;
}

/** 删除会员资产 GET /app/member/asset/delete/{memberAssetId} */
export async function deleteMemberAsset(memberAssetId: number): Promise<void> {
  if (!Number.isInteger(memberAssetId) || memberAssetId <= 0) {
    throw new Error('无效的资产 ID');
  }
  try {
    const res = await request<ResultResponse<void>>(
      `/app/member/asset/delete/${memberAssetId}`,
      {
        method: 'GET',
        skipErrorHandler: true,
      },
    );
    if (res?.code !== SUCCESS_CODE) {
      throw new Error(res?.message?.trim() || '删除失败');
    }
  } catch (error: unknown) {
    const data = (error as { response?: { data?: ResultResponse<void> } })?.response?.data;
    if (data?.message) {
      throw new Error(data.message);
    }
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error('删除失败');
  }
}

export async function uploadMemberAsset(
  file: File,
  options?: { workspaceId?: number },
): Promise<MemberAssetUploadResult> {
  const workspaceId = options?.workspaceId ?? getDefaultWorkspaceId();
  const formData = new FormData();
  formData.append('file', file);
  if (workspaceId != null) {
    formData.append('workspaceId', String(workspaceId));
  }

  try {
    const res = await request<ResultResponse<MemberAssetUploadResult>>(
      '/app/member/asset/upload',
      {
        method: 'POST',
        data: formData,
        skipErrorHandler: true,
      },
    );
    if (res?.code !== SUCCESS_CODE || !res.data) {
      throw new Error(res?.message?.trim() || '上传失败');
    }
    return res.data;
  } catch (error: unknown) {
    const data = (error as { response?: { data?: ResultResponse<MemberAssetUploadResult> } })
      ?.response?.data;
    if (data?.message) {
      throw new Error(data.message);
    }
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error('上传失败');
  }
}
